<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useQuery } from "@tanstack/vue-query";

import { listNotifications } from "@/api/client";
import {
  isWorkforceRoute,
  PROJECT_FEATURES,
  PROJECT_UTILITIES,
  WORKFORCE_FEATURES,
} from "@/modules";
import { useProjectStore } from "@/stores/project";
import AppHeader from "@/components/AppHeader.vue";

// `wide` opts a page out of the default 72rem reading width (used by the
// dashboard, which wants the full content area for its status grid).
defineProps<{ wide?: boolean }>();

const route = useRoute();
const drawerOpen = ref(false);

// Desktop-only: collapse the sidebar to icons-only, remembered across sessions.
const collapsed = ref(false);
try {
  collapsed.value = localStorage.getItem("sidebarCollapsed") === "1";
} catch {
  /* storage unavailable — default expanded */
}
function toggleCollapsed(): void {
  collapsed.value = !collapsed.value;
  try {
    localStorage.setItem("sidebarCollapsed", collapsed.value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

const projectStore = useProjectStore();
const { selectedProjectId } = storeToRefs(projectStore);

// The project id comes from the URL inside a project; keep the store synced so
// the project-scoped views' `?project_id=` queries keep working unchanged.
const projectId = computed(() => route.params.projectId as string | undefined);
watch(
  projectId,
  (id) => {
    if (id) projectStore.setProject(id);
  },
  { immediate: true },
);

const inWorkforce = computed(() => isWorkforceRoute(route.path));
// The projects list itself is a full-width page with no sidebar.
const showSidebar = computed(() => route.name !== "projects");

// Live count for the Notifications sidebar badge.
const { data: notifications } = useQuery({
  queryKey: ["notifications", selectedProjectId],
  queryFn: () =>
    selectedProjectId.value
      ? listNotifications(selectedProjectId.value)
      : Promise.resolve([]),
  enabled: () => !!selectedProjectId.value && !inWorkforce.value,
  refetchOnWindowFocus: true,
});
const notificationCount = computed(() => notifications.value?.length ?? 0);

watch(
  () => route.fullPath,
  () => {
    drawerOpen.value = false;
  },
);
</script>

<template>
  <div class="min-h-screen bg-neutral-50">
    <AppHeader @toggle-drawer="drawerOpen = !drawerOpen" />

    <div
      v-if="drawerOpen && showSidebar"
      class="fixed inset-0 z-30 bg-neutral-900/40 lg:hidden print:hidden"
      aria-hidden="true"
      @click="drawerOpen = false"
    />

    <aside
      v-if="showSidebar"
      class="fixed bottom-0 left-0 top-16 z-40 flex w-64 flex-col border-r border-neutral-200 bg-neutral-0 transition-all duration-200 ease-out lg:translate-x-0 print:hidden"
      :class="[
        drawerOpen ? 'translate-x-0' : '-translate-x-full',
        collapsed ? 'lg:w-16' : 'lg:w-64',
      ]"
    >
      <nav class="flex-1 space-y-6 overflow-y-auto px-3 py-4">
        <!-- Cross-project Workforce area -->
        <div
          v-if="inWorkforce"
          class="space-y-1"
        >
          <p
            class="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400"
            :class="collapsed ? 'lg:hidden' : ''"
          >
            Workforce
          </p>
          <RouterLink
            v-for="item in WORKFORCE_FEATURES"
            :key="item.to"
            :to="item.to"
            :title="item.label"
            class="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
            :class="[
              route.path === item.to ? 'bg-brand-primary/10 text-brand-primary' : '',
              collapsed ? 'lg:justify-center' : '',
            ]"
          >
            <svg
              class="h-5 w-5 shrink-0"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
            >
              <path :d="item.icon" />
            </svg>
            <span :class="collapsed ? 'lg:hidden' : ''">{{ item.label }}</span>
          </RouterLink>
        </div>

        <!-- Inside a project -->
        <div
          v-else-if="selectedProjectId"
          class="flex h-full flex-col"
        >
          <div class="space-y-1">
            <p
              class="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400"
              :class="collapsed ? 'lg:hidden' : ''"
            >
              Quality &amp; Inspection
            </p>
            <RouterLink
              v-for="item in PROJECT_FEATURES"
              :key="item.name"
              :to="{ name: item.name, params: { projectId: selectedProjectId } }"
              :title="item.label"
              class="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
              :class="[
                route.name === item.name ? 'bg-brand-primary/10 text-brand-primary' : '',
                collapsed ? 'lg:justify-center' : '',
              ]"
            >
              <svg
                class="h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <path :d="item.icon" />
              </svg>
              <span :class="collapsed ? 'lg:hidden' : ''">{{ item.label }}</span>
            </RouterLink>
          </div>

          <!-- Project "meta" utilities, pinned to the bottom behind a divider -->
          <div class="mt-auto space-y-1 border-t border-neutral-200 pt-4">
            <RouterLink
              v-for="item in PROJECT_UTILITIES"
              :key="item.name"
              :to="{ name: item.name, params: { projectId: selectedProjectId } }"
              :title="item.label"
              class="group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
              :class="[
                route.name === item.name ? 'bg-brand-primary/10 text-brand-primary' : '',
                collapsed ? 'lg:justify-center' : '',
              ]"
            >
              <svg
                class="h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <path :d="item.icon" />
              </svg>
              <span
                class="flex-1"
                :class="collapsed ? 'lg:hidden' : ''"
              >{{ item.label }}</span>
              <!-- expanded: numeric badge; collapsed: a small dot on the icon -->
              <span
                v-if="item.name === 'notifications' && notificationCount > 0"
                class="rounded-full bg-semantic-warning px-2 py-0.5 text-xs font-semibold text-neutral-0"
                :class="collapsed ? 'lg:hidden' : ''"
              >
                {{ notificationCount }}
              </span>
              <span
                v-if="collapsed && item.name === 'notifications' && notificationCount > 0"
                class="absolute right-2 top-2 hidden h-2 w-2 rounded-full bg-semantic-warning lg:block"
              />
            </RouterLink>
          </div>
        </div>
      </nav>

      <!-- Collapse / expand toggle (desktop only) -->
      <button
        type="button"
        class="hidden items-center gap-3 border-t border-neutral-200 px-3 py-3 text-sm font-medium text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900 lg:flex"
        :class="collapsed ? 'justify-center' : ''"
        :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="toggleCollapsed"
      >
        <svg
          class="h-5 w-5 shrink-0 transition-transform"
          :class="collapsed ? 'rotate-180' : ''"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          viewBox="0 0 24 24"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <span :class="collapsed ? 'lg:hidden' : ''">Collapse</span>
      </button>
    </aside>

    <div
      class="transition-all duration-200 print:!pl-0"
      :class="showSidebar ? (collapsed ? 'lg:pl-16' : 'lg:pl-64') : ''"
    >
      <main
        class="mx-auto px-4 py-8 sm:px-6 lg:px-8"
        :class="wide ? 'max-w-none' : 'max-w-6xl'"
      >
        <slot />
      </main>
    </div>
  </div>
</template>
